import React from 'react';

import { closestCenter, DndContext } from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PropTypes from 'prop-types';

import AttachmentDropZone from 'components/attachments/components/AttachmentDropZone';
import { AttachmentEditor } from 'components/attachments/components/AttachmentEditor';

import { resourceShape } from 'components/helpers/serialisableResources';

const columnStylings = ['tw-col-span-2', 'tw-col-span-3', 'tw-col-span-6'];

export default function AttachmentCollection({
  attachmentsQueryKeyUuid,
  currentAttachments,
  domainAttachments,
  onCaptionSave,
  onDeleteButtonClick,
  onFileUpload,
  onPreview,
  onReorder,
  onRotateSuccess,
  onSelectSize,
}) {
  const persistedAttachments = currentAttachments.filter(
    (attachment) => !attachment.hashid.startsWith('tmp-'),
  );

  const newAttachments = currentAttachments.filter((attachment) =>
    attachment.hashid.startsWith('tmp-'),
  );

  const orderedAttachmentHashids = [
    ...persistedAttachments,
    ...newAttachments,
  ].map((a) => a.hashid);

  const currentAttachmentsIndexedByHashId = currentAttachments.reduce(
    (acc, attachment) => {
      acc[attachment.hashid] = attachment;
      return acc;
    },
    {},
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id === over.id) return;
    onReorder({ active, over });
  };

  const isSortable = !!onReorder;
  const isDroppable = !!onFileUpload;

  const attachmentsContent = (
    <div className='tw-relative tw-mb-7'>
      <div className='tw-bg-gray-300 tw-pointer-events-none tw-absolute tw-inset-0 tw-z-0 tw-grid tw-h-full tw-grid-cols-6 tw-gap-4'>
        <div className='tw-z-10 tw-h-full tw-bg-grey-100' />
        <div className='tw-z-10 tw-h-full tw-bg-grey-100' />
        <div className='tw-z-10 tw-h-full tw-bg-grey-100' />
        <div className='tw-z-10 tw-h-full tw-bg-grey-100' />
        <div className='tw-z-10 tw-h-full tw-bg-grey-100' />
        <div className='tw-z-10 tw-h-full tw-bg-grey-100' />
      </div>

      <div className='tw-relative tw-z-10 tw-grid tw-grid-cols-6 tw-gap-4 tw-py-3'>
        {orderedAttachmentHashids.map((hashid) => {
          const domainAttachment = domainAttachments.data.find(
            (attachment) => attachment.attributes.hashid === hashid,
          );

          const coverImageId =
            domainAttachment?.relationships?.coverImage?.data?.id;
          const coverImage =
            coverImageId && domainAttachments.included.coverImage[coverImageId];

          const originalId =
            domainAttachment?.relationships?.original?.data?.id;
          const originalAttachment =
            domainAttachments.included?.attachment[originalId];

          return (
            <div
              className={
                columnStylings[
                  currentAttachmentsIndexedByHashId[hashid].displaySizeValue
                ]
              }
              data-element-name='attachment'
              key={hashid}
            >
              {isSortable ?
                <SortableAttachment
                  attachmentsQueryKeyUuid={attachmentsQueryKeyUuid}
                  coverImage={coverImage}
                  currentAttachment={currentAttachmentsIndexedByHashId[hashid]}
                  domainAttachment={domainAttachment}
                  onCaptionSave={onCaptionSave}
                  onDeleteButtonClick={onDeleteButtonClick}
                  onPreview={onPreview}
                  onRotateSuccess={onRotateSuccess}
                  onSelectSize={onSelectSize}
                  originalAttachment={originalAttachment}
                />
              : <ReadOnlyAttachment
                  attachmentsQueryKeyUuid={attachmentsQueryKeyUuid}
                  coverImage={coverImage}
                  currentAttachment={currentAttachmentsIndexedByHashId[hashid]}
                  domainAttachment={domainAttachment}
                  onPreview={onPreview}
                  originalAttachment={originalAttachment}
                />
              }
            </div>
          );
        })}
      </div>
    </div>
  );

  const content =
    isDroppable ?
      <AttachmentDropZone onUpload={onFileUpload}>
        {attachmentsContent}
      </AttachmentDropZone>
    : attachmentsContent;

  return isSortable ?
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={orderedAttachmentHashids}
          strategy={rectSortingStrategy}
        >
          {content}
        </SortableContext>
      </DndContext>
    : content;
}

function SortableAttachment({ currentAttachment, ...attachmentViewerProps }) {
  const {
    attributes: draggableAttributes,
    isDragging,
    listeners: draggableListeners,
    setNodeRef: setDraggableRefNode,
    transform: draggableTransform,
    transition: draggableTransition,
  } = useSortable({
    id: currentAttachment.hashid,
  });

  const style = {
    transform: CSS.Translate.toString(draggableTransform),
    transition: draggableTransition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div ref={setDraggableRefNode} style={style}>
      <AttachmentEditor
        {...attachmentViewerProps}
        currentAttachment={currentAttachment}
        draggable={{
          attributes: draggableAttributes,
          listeners: draggableListeners,
        }}
      />
    </div>
  );
}

function ReadOnlyAttachment({ ...attachmentViewerProps }) {
  return <AttachmentEditor {...attachmentViewerProps} />;
}

AttachmentCollection.propTypes = {
  attachmentsQueryKeyUuid: PropTypes.string.isRequired,
  currentAttachments: PropTypes.arrayOf(
    PropTypes.shape({
      displaySizeValue: PropTypes.number.isRequired,
      hashid: PropTypes.string.isRequired,
    }),
  ).isRequired,
  domainAttachments: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('attachment')).isRequired,
  }),
  onCaptionSave: PropTypes.func,
  onDeleteButtonClick: PropTypes.func,
  onFileUpload: PropTypes.func,
  onPreview: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  onRotateSuccess: PropTypes.func,
  onSelectSize: PropTypes.func,
};
